'use client';

/**
 * Full-bleed image showcase hero — ONE background image with copy
 * overlaid directly on it (gradient scrim, no separate panel), and a
 * right-hand rail of picks that DRIVE both the visual and the copy:
 * clicking a rail item crossfades the background to that item's
 * image and swaps the badge/title/description/CTA shown on the left
 * for that item's own. A stat bar (icon-box + label) runs along the
 * bottom, independent of the active pick.
 *
 * <pre>
 *   ┌───────────────────────────────────────────────────────┐
 *   │▓▓▓ Badge                                       ┌──────┐│
 *   │▓▓▓ Big title            background image        │[thumb]││ ← active
 *   │▓▓▓ long description                             │Title ││
 *   │▓▓▓ [ CTA → ]  (only if the active item has href)│short  ││
 *   │                                                  ├──────┤│
 *   │                                                  │[thumb]│
 *   ├───────────────────────────────────────────────────────┤
 *   │ [icon] stat    [icon] stat    [icon] stat               │
 *   └───────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>Each item carries two blurbs: {@code subtitle} (short — shown on
 * its own rail card) and {@code description} (long — shown in the
 * overlaid copy while that item is active). {@code title} is a
 * fallback used only when the active item doesn't supply its own —
 * e.g. no items configured yet. The CTA button only renders when the
 * active item has an {@code href}.
 */
import { useEffect, useRef, useState } from 'react';
import { BookOpen, Users, Gift, Award, Star } from 'lucide-react';
import { tx, asList } from '../_shared/coerce';
import { Button } from '@akademi/ui';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

/** Hero content-row height — the stat bar adds on top of this. */
const HERO_HEIGHT = '400px';

// Candidate widths for the visual's srcSet — the rail (see `.hplr-rail`
// below, clamp(240px, 24vw, 320px) wide) sits beside the visual above
// 1100px; below that the layout stacks and the visual goes full-width
// (see the `sizes` attribute on the <img> below).
const IMAGE_SRCSET_WIDTHS = [480, 800, 1200, 1600];

type Localized = string | Record<string, string>;

const STAT_ICONS = {
  courses: BookOpen,
  students: Users,
  free: Gift,
  certificate: Award,
  rating: Star,
} as const;

/** Small colour pill — used in the rail tiles and the active-item badge. */
function RailBadge({
  label,
  color,
  size = 'sm',
}: {
  label: string;
  color?: string;
  size?: 'sm' | 'lg';
}) {
  const isLg = size === 'lg';
  return (
    <span
      style={{
        flexShrink: 0,
        alignSelf: 'flex-start',
        display: 'inline-block',
        fontSize: isLg ? 'var(--font-size-xs, 12px)' : '10px',
        fontWeight: 'var(--font-weight-semibold, 600)',
        lineHeight: 1,
        letterSpacing: isLg ? 'var(--font-tracking-wider, 0.04em)' : undefined,
        textTransform: isLg ? 'uppercase' : undefined,
        padding: isLg ? '5px 12px' : '3px 7px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: color || 'var(--color-brand-500, #3b5bdb)',
        color: '#ffffff',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export interface HeroPlaylistRailItem {
  title?: Localized;
  /** Short blurb shown on the rail card itself (single line, ellipsised). */
  subtitle?: Localized;
  /** Longer blurb shown in the overlaid copy while this item is active. */
  description?: Localized;
  image?: string;
  /** When set, a CTA button linking here appears under the active item's copy. */
  href?: string;
  /** CTA button label. Defaults to "Learn more". */
  ctaLabel?: Localized;
  badge?: Localized;
  /** Any CSS colour. Defaults to the brand colour. */
  badgeColor?: string;
}

export interface HeroPlaylistRailStat {
  icon?: keyof typeof STAT_ICONS;
  label?: Localized;
}

export type HeroPlaylistRailContentPosition = 'top' | 'center' | 'bottom';

export interface HeroPlaylistRailProps {
  /** Fallback title, used only when the active item has none of its own. */
  title?: Localized;
  stats?: HeroPlaylistRailStat[];
  items?: HeroPlaylistRailItem[];
  /** Item selected on first render. */
  defaultActiveIndex?: number;
  /** Vertical alignment of the overlaid copy block. Default 'center'. */
  contentPosition?: HeroPlaylistRailContentPosition;
  /** Auto-advance through rail items. Default false. */
  autoPlay?: boolean;
  /** Seconds between auto-advances. Default 6. */
  autoPlayInterval?: number;
  paddingOuter?: PaddingBox;
}

const JUSTIFY_BY_POSITION: Record<HeroPlaylistRailContentPosition, string> = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

export function HeroPlaylistRail({
  title,
  stats,
  items,
  defaultActiveIndex = 0,
  contentPosition = 'center',
  autoPlay = false,
  autoPlayInterval = 6,
  paddingOuter,
}: HeroPlaylistRailProps) {
  const railItems = asList<HeroPlaylistRailItem>(items).slice(0, 5);
  const statList = asList<HeroPlaylistRailStat>(stats);

  const [active, setActive] = useState(
    Math.min(Math.max(defaultActiveIndex, 0), Math.max(railItems.length - 1, 0)),
  );
  const activeItem = railItems[active];
  const visualImage = activeItem?.image;

  const displayBadge = activeItem?.badge;
  const displayBadgeColor = activeItem?.badgeColor;
  const displayTitle = activeItem?.title ?? title;
  const displayDescription = activeItem?.description;
  const displayHref = activeItem?.href;
  const displayCtaLabel = activeItem?.ctaLabel;

  // Auto-advance honouring prefers-reduced-motion + tab visibility — same
  // pattern as HeroFeatureRail's autoPlay.
  const reducedMotionRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const autoPlaySeconds = Math.max(2, autoPlayInterval);

  useEffect(() => {
    if (!autoPlay) return;
    if (railItems.length <= 1) return;
    const intervalMs = autoPlaySeconds * 1000;
    const id = window.setInterval(() => {
      if (reducedMotionRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setActive((i) => (i + 1) % railItems.length);
    }, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [autoPlay, autoPlaySeconds, railItems.length]);

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-6, 24px) clamp(var(--space-4, 16px), 3vw, var(--space-8, 32px))',
        ...resolvePaddingStyle(paddingOuter),
      }}
    >
      <div
        className="hplr-outer"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          height: HERO_HEIGHT,
          gap: 0,
        }}
      >
        <PortalLink
          href={displayHref}
          style={{ flex: '1 1 auto', minWidth: 0, textDecoration: 'none' }}
        >
          <div
            className="hplr-shell"
            style={{
              position: 'relative',
              flex: '1 1 auto',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderTopLeftRadius: 'var(--radius-lg, 18px)',
              borderBottomLeftRadius: 'var(--radius-lg, 18px)',
              overflow: 'hidden',
              height: HERO_HEIGHT,
              background: 'var(--color-surface-card, #0b0f19)',
            }}
          >
            {/* Background — the whole hero is one photo; the rail swaps it. */}
            {visualImage && (
              <img
                key={active}
                src={resizeImageUrl(visualImage, '1600x0')}
                srcSet={IMAGE_SRCSET_WIDTHS.map(
                  (w) => `${resizeImageUrl(visualImage, `${w}x0`)} ${w}w`,
                ).join(', ')}
                sizes="(min-width: 1100px) calc(100vw - 320px), 100vw"
                alt=""
                // This hero is typically the first thing on the page (LCP
                // candidate) and only ever shows one image at a time — always
                // worth prioritizing over whatever loads after it.
                fetchPriority="high"
                decoding="async"
                className="hplr-fade-img"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}

            {/* Scrim — dark on the left (copy) only, clear for the rest of the
             *  photo (rail now lives outside the shell so no right-side scrim). */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(90deg, rgba(4,8,18,0.92) 0%, rgba(4,8,18,0.72) 30%, rgba(4,8,18,0.18) 52%, rgba(4,8,18,0) 68%, rgba(4,8,18,0) 100%)',
              }}
            />

            {/* Content — copy overlaid on the photo (rail now lives outside the shell). */}
            <div
              className="hplr-content"
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              <div
                className="hplr-copy"
                style={{
                  flex: '1 1 560px',
                  maxWidth: '620px',
                  padding: 'clamp(var(--space-5, 20px), 3.4vw, var(--space-9, 36px))',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: JUSTIFY_BY_POSITION[contentPosition] ?? 'center',
                }}
              >
                {(displayBadge || displayTitle || displayDescription || displayHref) && (
                  <div
                    key={active}
                    className="hplr-fade-caption"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-3-5, 14px)',
                    }}
                  >
                    {displayBadge && (
                      <RailBadge label={tx(displayBadge)} color={displayBadgeColor} size="lg" />
                    )}
                    {displayTitle && (
                      <h2
                        style={{
                          margin: 0,
                          fontSize: 'clamp(28px, 2.5vw, 30px)',
                          fontWeight: 'var(--font-weight-extrabold, 800)',
                          lineHeight: 'var(--line-height-tight, 1.08)',
                          letterSpacing: 'var(--font-tracking-snug, -0.015em)',
                          color: '#ffffff',
                          fontFamily: 'var(--font-family-display, inherit)',
                          textShadow: '0 2px 16px rgba(0,0,0,0.35)',
                        }}
                      >
                        {tx(displayTitle)}
                      </h2>
                    )}
                    {displayDescription && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 'var(--font-size-sm, 14px)',
                          lineHeight: 'var(--line-height-relaxed, 1.55)',
                          color: 'rgba(255,255,255,0.78)',
                          maxWidth: '50ch',
                        }}
                      >
                        {tx(displayDescription)}
                      </p>
                    )}
                    {displayCtaLabel && (
                      <Button
                        href={displayHref}
                        radius="pill"
                        size="sm"
                        style={{
                          alignSelf: 'flex-start',
                          //height: 'auto',
                          //padding: 'var(--space-3, 12px) var(--space-5, 20px)',
                          //background: 'var(--color-brand-500, #3b5bdb)',
                          //color: 'var(--color-text-on-brand, #fff)',
                        }}
                      >
                        {displayCtaLabel ? tx(displayCtaLabel) : 'Learn more'}
                        <span aria-hidden>→</span>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* BOTTOM — stat bar (icon-box + label), width-matched to the copy
             *  column so it sits under the text only, not under the rail. */}
            {statList.length > 0 && (
              <div
                className="hplr-stats"
                style={{
                  position: 'relative',
                  zIndex: 1,
                  flexShrink: 0,
                  maxWidth: '620px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-5, 20px)',
                  padding:
                    'clamp(var(--space-1, 4px), 0.8vw, var(--space-2, 8px)) clamp(var(--space-5, 20px), 3.4vw, var(--space-9, 36px))',
                }}
              >
                {statList.map((stat, i) => {
                  const Icon = STAT_ICONS[stat.icon ?? 'courses'] ?? BookOpen;
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-2, 8px)',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          borderRadius: 'var(--radius-md, 10px)',
                          background: 'rgba(255,255,255,0.12)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          display: 'grid',
                          placeItems: 'center',
                          color: '#ffffff',
                        }}
                      >
                        <Icon size={16} aria-hidden />
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs, 13px)',
                          fontWeight: 'var(--font-weight-medium, 500)',
                          color: 'rgba(255,255,255,0.88)',
                        }}
                      >
                        {tx(stat.label)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PortalLink>

        {/* RIGHT — clickable rail; picking a row swaps the background + copy. */}
        {railItems.length > 0 && (
          <div
            className="hplr-rail"
            style={{
              flexShrink: 0,
              width: 'clamp(240px, 24vw, 320px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2, 8px)',
              padding: 'clamp(var(--space-4, 16px), 2.4vw, var(--space-6, 24px))',
              borderTopRightRadius: 'var(--radius-lg, 18px)',
              borderBottomRightRadius: 'var(--radius-lg, 18px)',
              background: 'var(--color-surface-card, #0b0f19)',
              overflowY: 'auto',
            }}
          >
            {railItems.map((item, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActive(i)}
                  className="hplr-rail-item"
                  style={{
                    flex: '1 1 0',
                    minHeight: 0,
                    maxHeight: '64px',
                    display: 'grid',
                    gridTemplateColumns: '84px 1fr',
                    gap: 'var(--space-2-5, 10px)',
                    padding: 'var(--space-2, 8px)',
                    borderRadius: 'var(--radius-md, 12px)',
                    background: isActive
                      ? 'color-mix(in srgb, var(--color-text-primary) 12%, var(--color-surface-card))'
                      : 'color-mix(in srgb, var(--color-text-primary) 6%, var(--color-surface-card))',
                    border: `1px solid ${isActive ? 'var(--color-brand-300, #9db4ff)' : 'var(--color-border-default)'}`,
                    color: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    font: 'inherit',
                    alignItems: 'center',
                  }}
                >
                  <div
                    className="hplr-rail-thumb"
                    style={{
                      height: '100%',
                      minHeight: 0,
                      borderRadius: 'var(--radius-sm, 8px)',
                      background: item.image
                        ? `url("${resizeImageUrl(item.image, '160x0')}") center/cover`
                        : 'var(--color-surface-muted)',
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 'var(--space-1-5, 6px)',
                      }}
                    >
                      {item.title && (
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs, 13px)',
                            fontWeight: 'var(--font-weight-semibold, 600)',
                            lineHeight: 'var(--line-height-normal, 1.3)',
                            color: 'var(--color-text-primary)',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {tx(item.title)}
                        </div>
                      )}
                      {item.badge && <RailBadge label={tx(item.badge)} color={item.badgeColor} />}
                    </div>
                    {item.subtitle && (
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs, 12px)',
                          color: 'var(--color-text-secondary)',
                          marginTop: 'var(--space-0-5, 2px)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {tx(item.subtitle)}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 1100px) {
          .hplr-outer { flex-direction: column !important; height: auto !important; }
          .hplr-shell {
            height: auto !important;
            min-height: 0 !important;
            border-radius: var(--radius-lg, 18px) var(--radius-lg, 18px) 0 0 !important;
          }
          .hplr-content { min-height: 0 !important; flex-direction: column !important; }
          .hplr-copy { max-width: none !important; flex: none !important; }
          .hplr-stats { max-width: none !important; }
          .hplr-rail {
            width: auto !important;
            height: auto !important;
            flex-direction: row !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            border-radius: 0 0 var(--radius-lg, 18px) var(--radius-lg, 18px) !important;
          }
          .hplr-rail-item { flex: 0 0 260px !important; height: auto !important; }
          .hplr-rail-thumb { height: auto !important; aspect-ratio: 16 / 9; }
        }
        @media (max-width: 560px) {
          .hplr-rail-item { flex: 0 0 220px !important; }
        }

        @keyframes hplr-fade-in-img {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .hplr-fade-img {
          animation: hplr-fade-in-img var(--motion-duration-normal, 320ms) var(--motion-ease-out, ease) both;
        }
        .hplr-fade-caption {
          animation: hplr-fade-in-img var(--motion-duration-normal, 320ms) var(--motion-ease-out, ease) both;
        }

        .hplr-rail-item {
          transition:
            border-color var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease),
            background var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease);
        }
        .hplr-rail-item:hover {
          border-color: var(--color-brand-300, #9db4ff);
        }

        @media (prefers-reduced-motion: reduce) {
          .hplr-fade-img { animation: none; }
          .hplr-fade-caption { animation: none; }
          .hplr-rail-item { transition: none; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
